import { gql } from "lib/apolloClient";
import {
  courseFragment,
  gradeFragment,
  studentFragment,
  staffFragment,
  attachmentFragment,
  taggedPracticesFragment,
  studentResponseFragment,
  academicYearFragment,
  curriculumProgramFragment,
  studentsAlertFragment,
} from "modules/CommonFragments";
import { plannerFieldFragment } from "UnitPlans/modules/UnitPlanFragments";

// Fragments for planner element queries in progress reports
export const plannerElementSetBasicFragment = {
  plannerElementSetBasic: gql`
    fragment plannerElementSetBasicItem on PlannerElementSet {
      id
      label
      type
    }
  `,
  genericPlannerElementSetBasic: gql`
    fragment genericPlannerElementSetBasicItem on GenericPlannerElementSet {
      id
      label
      type
    }
  `,
};

export const pypElementFragment = {
  lp: gql`
    fragment lpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isArchived
          isGraded
        }
      }
    }
  `,
  concept: gql`
    fragment conceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isArchived
          isGraded
        }
      }
    }
  `,
  atl: gql`
    fragment atlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      grades {
        id
        name
      }
      dataSetNodeWrapper {
        properties {
          isArchived
          isGraded
        }
      }
    }
  `,
};

export const pypPlannerElementSetFragment = {
  lpSet: gql`
    fragment lpSetItem on PlannerLearnerProfileSet {
      id
      nodes {
        ...lpItem
      }
    }
    ${pypElementFragment.lp}
  `,
  conceptSet: gql`
    fragment conceptSetItem on PlannerConceptSet {
      id
      nodes {
        ...conceptItem
      }
    }
    ${pypElementFragment.concept}
  `,
  atlSet: gql`
    fragment atlSetItem on ATLSet {
      id
      nodes(filters: $atlFilters) {
        ...atlItem
      }
    }
    ${pypElementFragment.atl}
  `,
};

export const plannerElementNodeMinimalFragment = {
  plannerElementNodeMinimal: gql`
    fragment plannerElementNodeMinimalItem on PlannerElementNode {
      id
      label
      subText
      code
      type
      depth
      children
      parent
      isLeaf
      dataSetNodeWrapper {
        properties {
          isArchived
        }
      }
    }
  `,
};

export const plannerElementSetWithMinimalNodesFragment = {
  plannerElementSetWithMinimalNodes: gql`
    fragment plannerElementSetWithMinimalNodesItem on PlannerElementSet {
      id
      label
      type
      nodes(filters: $filters) {
        ...plannerElementNodeMinimalItem
      }
    }
    ${plannerElementNodeMinimalFragment.plannerElementNodeMinimal}
  `,
  plannerElementSetWithNodes: gql`
    fragment plannerElementSetWithNodesItem on GenericPlannerElementSet {
      id
      label
      type
      ... on ATLSet {
        ...atlSetItem
      }
      ... on PlannerLearnerProfileSet {
        ...lpSetItem
      }
      ... on PlannerConceptSet {
        ...conceptSetItem
      }
    }
    ${pypPlannerElementSetFragment.atlSet}
    ${pypPlannerElementSetFragment.lpSet}
    ${pypPlannerElementSetFragment.conceptSet}
  `,
};

export const studentProgressReportAttendanceSummaryFragment = {
  studentProgressReportAttendanceSummary: gql`
    fragment studentProgressReportAttendanceSummaryItem on StudentProgressReportAttendanceV2 {
      id
      attendanceType
      attendanceTypeId
      attendanceDataFor
      metric
      attendanceCount
      attendanceOptionId
      entityType
      entityId
      subjectId
      academicTermId
    }
  `,
};

export const studentProgressReportAttendanceFragment = {
  studentProgressReportAttendanceV2: gql`
    fragment studentProgressReportAttendanceItemV2 on StudentProgressReportAttendanceWrapper {
      startDate
      endDate
      numberOfOperationalDays
      attendanceSummary {
        ...studentProgressReportAttendanceSummaryItem
      }
    }
    ${studentProgressReportAttendanceSummaryFragment.studentProgressReportAttendanceSummary}
  `,
};

export const progressReportRatingFragment = {
  progressReportRating: gql`
    fragment progressReportRatingItem on StudentProgressReportPypElementRating {
      id
      nodeId
      value
      isRemoved
      academicCriteriaSet
      academicCriteriaValue
      ibPYPElementId
      ibPYPElementType
      isComputedRating
      academicTermId
      categoryId
    }
  `,
};

export const gradePointFragment = {
  gradePoint: gql`
    fragment gradePointItem on GradePoint {
      id
      value
      subjectId
      academicTermId
    }
  `,
  gradePointV2: gql`
    fragment gradePointItem on GradePointV2 {
      id
      value
      entityType
      entityId
      academicTermId
    }
  `,
};

export const gpaRatingFragment = {
  gpaRating: gql`
    fragment gpaRatingItem on GradePointAverage {
      id
      value
      academicTermId
    }
  `,
  gpaRatingV2: gql`
    fragment gpaRatingItem on GradePointAverageV2 {
      id
      value
      academicTermId
    }
  `,
};

export const creditFragment = {
  credit: gql`
    fragment creditItem on Credit {
      id
      value
      subjectId
      academicTermId
    }
  `,
  creditV2: gql`
    fragment creditItem on GradeSubjectTermCreditMap {
      id
      credit
      subject {
        id
      }
      learningCourse {
        id
      }
      academicTerm {
        id
      }
      genericTag {
        id
      }
    }
  `,
};

export const qualityPointFragment = {
  qualityPoint: gql`
    fragment qualityPointItem on QualityPoint {
      id
      value
      subjectId
      academicTermId
    }
  `,
  qualityPointV2: gql`
    fragment qualityPointItem on QualityPointV2 {
      id
      value
      entityType
      entityId
      academicTermId
    }
  `,
};

export const aggregateRatingFragment = {
  aggregateRating: gql`
    fragment aggregateRatingItem on StudentProgressReportPypElementRating {
      id
      value
      isRemoved
      academicCriteriaSet
      academicCriteriaValue
      ibPYPElementId
      ibPYPElementType
      isComputedRating
      academicTermId
      categoryId
    }
  `,
};

export const gpaExclusionMapFragment = {
  basicGpaExclusionMapDetails: gql`
    fragment gpaExclusionMapItem on GPAExclusionMap {
      id
      grade {
        id
      }
      subject {
        id
      }
      isExcludedFromGPA
    }
  `,
};

export const gpaInclusionMapFragment = {
  basicGpaInclusionMapDetails: gql`
    fragment gpaInclusionMapItem on GPAInclusionMap {
      id
      grade {
        id
      }
      subject {
        id
      }
      learningCourse {
        id
      }
    }
  `,
};

export const gpaAddedValuesFragment = {
  basicGpaAddedValuesDetails: gql`
    fragment gpaAddedValueItem on GPAAddedValue {
      id
      grade {
        id
      }
      subject {
        id
      }
      value
      genericTag {
        id
      }
      learningCourse {
        id
      }
    }
  `,
};

export const gpaAssociatedDataFragment = {
  gpaAssociatedData: gql`
    fragment gpaAssociatedDataItem on GPAAssociatedData {
      gpRatings {
        ...gradePointItem
      }
      gpaRatings {
        ...gpaRatingItem
      }
      credits {
        ...creditItem
      }
      qualityPoints {
        ...qualityPointItem
      }
      aggregateRatings {
        ...aggregateRatingItem
      }
      gpaExclusionMap {
        ...gpaExclusionMapItem
      }
      gpaAddedValues {
        ...gpaAddedValueItem
      }
    }
    ${gradePointFragment.gradePoint}
    ${gpaRatingFragment.gpaRating}
    ${creditFragment.credit}
    ${qualityPointFragment.qualityPoint}
    ${aggregateRatingFragment.aggregateRating}
    ${gpaExclusionMapFragment.basicGpaExclusionMapDetails}
    ${gpaAddedValuesFragment.basicGpaAddedValuesDetails}
  `,
  gpaAssociatedContextualData: gql`
    fragment gpaAssociatedContextualData on GPAAssociatedDataV2 {
      gpRatings {
        ...gradePointItem
      }
      gpaRatings {
        ...gpaRatingItem
      }
      credits {
        ...creditItem
      }
      qualityPoints {
        ...qualityPointItem
      }
      aggregateRatings {
        ...aggregateRatingItem
      }
      gpaInclusionMap {
        ...gpaInclusionMapItem
      }
      gpaAddedValues {
        ...gpaAddedValueItem
      }
    }
    ${gradePointFragment.gradePointV2}
    ${gpaRatingFragment.gpaRatingV2}
    ${creditFragment.creditV2}
    ${qualityPointFragment.qualityPointV2}
    ${aggregateRatingFragment.aggregateRating}
    ${gpaInclusionMapFragment.basicGpaInclusionMapDetails}
    ${gpaAddedValuesFragment.basicGpaAddedValuesDetails}
  `,
};

export const progressReportRemarkFragment = {
  progressReportRemark: gql`
    fragment progressReportRemarkItem on StudentProgressReportRemark {
      id
      nodeId
      remarkType
      message
      createdBy {
        ...staffItem
      }
      createdAt
      attachments {
        ... on Attachment {
          ...attachmentItem
        }
        ... on Post {
          id
          title
          attachments {
            ...attachmentItem
          }
          items {
            item {
              id
              ...studentResponseItem
            }
            itemType
          }
        }
      }
    }
    ${attachmentFragment.attachment}
    ${staffFragment.basicStaffDetails}
    ${studentResponseFragment.studentResponse}
  `,
};

export const organizationGeneratedTemplateFragment = {
  organizationGeneratedTemplate: gql`
    fragment organizationGeneratedTemplateItem on Organization {
      id
      progressReportGeneratedTemplate(
        grades: $grades
        curriculumType: $curriculumType
        curriculumProgramId: $curriculumProgramId
        type: $type
        baseTemplate: $baseTemplate
        gradingPeriodType: $gradingPeriodType
        baseTemplateId: $baseTemplateId
        locale: $locale
        createdAt: $createdAt
        academicYearId: $academicYearId
      )
    }
  `,
};

export const progressReportTemplateFragment = {
  progressReportTemplateFeed: gql`
    fragment progressReportTemplateItem on ProgressReportTemplate {
      id
      name
      progressReportTaskGroups {
        id
        title
        academicYear {
          ...academicYearItem
        }
        courseProgressReportTasks {
          id
          course {
            ...courseItem
          }
          parentEntity {
            id
            ... on Grade {
              id
              name
            }
          }
        }
      }
      grades {
        ...gradeItem
      }
      updatedAt
      filteredNodes
      gradingPeriodType
      locale
      version
    }
    ${courseFragment.basicCourserDetails}
    ${gradeFragment.basicGradeDetails}
    ${academicYearFragment.academicYear}
  `,
  progressReportTemplateDetails: gql`
    fragment progressReportTemplateItem on ProgressReportTemplate {
      id
      name @skip(if: $skipParam)
      grades @skip(if: $skipParam) {
        ...gradeItem
      }
      gradingPeriodType @skip(if: $skipParam)
      body
      locale
      baseTemplateId
      createdAt
    }
    ${gradeFragment.basicGradeDetails}
  `,
  progressReportTemplateAcademicYearWiseFeed: gql`
    fragment progressReportTemplateItem on ProgressReportTemplate {
      id
      name
      grades {
        ...gradeItem
      }
      updatedAt
      gradingPeriodType
      locale
      academicYear {
        ...academicYearItem
      }
    }
    ${gradeFragment.basicGradeDetails}
    ${academicYearFragment.academicYear}
  `,
};

export const organizationProgressReportTemplateFragment = {
  organizationProgressReportTemplate: gql`
    fragment organizationProgressReportTemplateItem on Organization {
      id
      progressReportTemplates(filters: $filters) {
        totalCount
        edges {
          node {
            ...progressReportTemplateItem
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
    ${progressReportTemplateFragment.progressReportTemplateFeed}
  `,
  organizationProgressReportTemplateCount: gql`
    fragment organizationProgressReportTemplateItem on Organization {
      id
      progressReportTemplates(filters: $filters) {
        totalCount
      }
    }
  `,
  organizationProgressReportTemplateAcademicYearWise: gql`
    fragment organizationProgressReportTemplateItem on Organization {
      id
      progressReportTemplates(filters: $filters) {
        totalCount
        edges {
          node {
            ...progressReportTemplateItem
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
    ${progressReportTemplateFragment.progressReportTemplateAcademicYearWiseFeed}
  `,
};

export const progressReportSignatoriesFragment = {
  progressReportSignatories: gql`
    fragment progressReportSignatoriesItem on Signatory {
      id
      name
      designation
      signatureImage
      displaySequence
    }
  `,
};

export const progressReportFragment = {
  progressReportFeed: gql`
    fragment progressReportItem on CourseProgressReportTask {
      id
      title
      completionStatus
      gradeSubjectObjectivesIsComplete
      studentProgressReport {
        id
        status
        student {
          id
        }
      }
      course {
        ...courseItem
        grades {
          id
        }
      }
      parentEntity {
        id
        ... on Grade {
          id
          name
        }
      }
      unitPlans {
        id
        title {
          id
          value
        }
        image {
          id
          resolvedMinimalTree {
            ... on ResolvedFieldVideoAttachment {
              id
              attachment {
                id
                thumbUrl
                url
              }
            }
          }
        }
        unitType {
          id
          value
        }
        subjects {
          id
          value
        }
        theme {
          id
          value
          resolvedMinimalTree {
            ... on ResolvedFieldThemeSet {
              id
              type
              themes {
                id
                label
              }
            }
          }
        }
      }
      filteredNodes
      deadlineDate
      progressReportTaskGroup {
        id
        attendanceMode
        academicTerm {
          id
          label
        }
        academicTerms {
          id
          label
        }
      }
      attendanceDates {
        startDate
        endDate
      }
      reportingDates {
        startDate
        endDate
      }
      reportStatusCount {
        status
        count
      }
      createdAt
      signatories {
        ...progressReportSignatoriesItem
      }
    }
    ${courseFragment.basicCourserDetails}
    ${progressReportSignatoriesFragment.progressReportSignatories}
  `,
};

export const reportCourseFragments = {
  basicCourserDetails: gql`
    fragment courseItem on Course {
      id
      title
      isArchived
      grades {
        id
        name
      }
    }
  `,
};

export const progressReportTaskGroupFragment = {
  ProgressReportTaskGroupFeed: gql`
    fragment progressReportTaskGroupItem on ProgressReportTaskGroup {
      id
      title
      subtitle
      attendanceMode
      studentReflectionAuthor
      studentReflectionDeadline
      createdAt
      deadlineDate
      footerDate
      pdfFilenameFormat
      reportingDates {
        startDate
        endDate
      }
      courseProgressReportTasks {
        id
        canArchive
        course {
          ...courseItem
        }
        parentEntity {
          id
          ... on Grade {
            id
            name
          }
        }
      }
      template {
        id
        name
        grades {
          id
        }
        gradingPeriodType
      }
      filteredNodes
      academicTerm {
        id
        label
      }
      academicTerms {
        id
        label
      }
    }
    ${reportCourseFragments.basicCourserDetails}
  `,
  progressReportTaskObjective: gql`
    fragment progressReportTaskObjectiveItem on ProgressReportTaskGroup {
      id
      title
      courseProgressReportTasks(filters: $courseProgressReportTaskFilters) {
        id
        parentEntity {
          id
          ... on Grade {
            id
            name
          }
        }
        reportSubjects {
          id
          name
          associatedChildPlannerElementNodes(
            filters: $associatedChildPlannerElementFilters
          ) {
            id
            label
          }
        }
        studentProgressReport(filters: $studentReportFilters) {
          id
          student {
            id
            firstName
            middleName
            lastName
            profileImage
            isArchived
            isArchivedV2(filters: $userArchivedFilters)
            prefix
          }
          plannerElementNodes(filters: { elementType: MYP_OBJECTIVES }) {
            id
            label
            associatedParents {
              id
              ... on Subject {
                id
                name
              }
            }
          }
          mypCriteria(filters: $mypCriteriaFilters) {
            id
            label
            associatedParents {
              id
              ... on Subject {
                id
                name
              }
            }
          }
          reportSubjects {
            id
          }
          status
        }
      }
    }
  `,
};

export const setProgressReportFragment = {
  setProgressReport: gql`
    fragment setProgressReportFragmentItem on ProgressReportTaskGroup {
      id
      title
      subtitle
      attendanceMode
      academicTerms {
        id
        lockingStatus
        associatedAcademicTermEntities {
          id
          academicTerm {
            id
            lockingStatus
          }
        }
      }
      academicYearId
      template {
        id
        gradingPeriodType
      }
      courseProgressReportTasks(searchText: $searchText) {
        ...progressReportItem
      }
    }
    ${progressReportFragment.progressReportFeed}
  `,
};

export const organizationProgressReportGroupFragment = {
  organizationProgressReportGroup: gql`
    fragment organizationProgressReportGroupItem on Organization {
      id
      progressReportTaskGroups(
        first: 12
        after: $after
        filters: {
          searchText: $searchText
          academicYearId: $academicYearId
          curriculumProgramIds: $curriculumProgramIds
        }
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...progressReportTaskGroupItem
          }
        }
      }
    }
    ${progressReportTaskGroupFragment.ProgressReportTaskGroupFeed}
  `,
  organizationProgressReportGroupItem: gql`
    fragment organizationProgressReportGroupItem on Organization {
      id
      progressReportTaskGroups(
        first: 12
        after: $after
        filters: {
          searchText: $searchText
          academicYearId: $academicYearId
          curriculumProgramIds: $curriculumProgramIds
        }
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title
            subtitle
            attendanceMode
            studentReflectionAuthor
            studentReflectionDeadline
            createdAt
            deadlineDate
            footerDate
            pdfFilenameFormat
            reportingDates {
              startDate
              endDate
            }
            courseProgressReportTasks {
              id
              canArchive
              course {
                id
                title
                isArchived
                grades {
                  id
                  name
                }
              }
              parentEntity {
                id
                ... on Grade {
                  id
                  name
                }
              }
            }
            template {
              id
              name
              version
              grades {
                id
              }
              gradingPeriodType
            }
            academicTerm {
              id
              label
            }
            academicTerms {
              id
              label
            }
          }
        }
      }
    }
  `,
};

export const courseProgressReportFragment = {
  courseProgressReportFeed: gql`
    fragment courseProgressReportItem on Course {
      id
      courseProgressReportTasks(
        filters: { searchText: $searchText, academicTermIds: $academicTermIds }
      ) {
        totalCount
        edges {
          node {
            ...progressReportItem
          }
        }
      }
    }
    ${progressReportFragment.progressReportFeed}
  `,
  courseProgressReportFeedCount: gql`
    fragment courseProgressReportItem on Course {
      id
      courseProgressReportTasks {
        totalCount
      }
    }
  `,
  progressReportCourseSectionCompletionStats: gql`
    fragment progressReportCourseSectionCompletionStatsItem on ProgressReportCourseSectionCompleteStats {
      id
      total
      pending
      completed
      notAssessed
      course {
        id
        title
        subjects {
          id
          name
        }
      }
      sections
      courseProgressReportTask {
        id
        title
      }
    }
  `,
};

export const studentProgressReportFragment = {
  studentProgressReportFeed: gql`
    fragment studentProgressReportItem on StudentProgressReport {
      id
      status
      studentReflectionStatus
      sectionCompleteStatus {
        section
        isCompleted
      }
      student {
        ...studentWithBasicCourseItem
        alertTypes {
          ...alertTypeFields
        }
        isArchivedV2(filters: $userArchivedFilters)
        prefix
      }
      courseProgressReportTask {
        id
        title
        course {
          ...courseItem
          grades {
            id
          }
        }
        reportingDates {
          startDate
          endDate
        }
        parentEntity {
          id
          ... on Grade {
            id
            name
          }
          ... on Course {
            id
            title
          }
        }
        progressReportTaskGroup {
          id
          attendanceMode
        }
      }
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
      publishedReport {
        id
      }
      isSubjectObjectivesSelected
    }
    ${courseFragment.basicCourserDetails}
    ${studentFragment.basicStudentDetailsWithBasicCourse}
    ${taggedPracticesFragment.taggedPractices}
    ${studentsAlertFragment}
  `,
  studentProgressReportStatus: gql`
    fragment studentProgressReportStatusItem on StudentProgressReport {
      id
      status
      isSubjectObjectivesSelected
    }
  `,
  studentProgressReportPublishedTemplate: gql`
    fragment studentProgressReportItem on StudentProgressReport {
      id
      publishedReport {
        id
        template(filters: $templateFilters)
      }
    }
  `,
  studentProgressReportTemplate: gql`
    fragment studentProgressReportItem on StudentProgressReport {
      id
      template(filters: $templateFilters)
    }
  `,
  studentProgressReportDetails: gql`
    fragment studentProgressReportItem on StudentProgressReport {
      id
      student {
        ...studentItem
        prefix
      }
      attendanceSummaryV2 {
        ...studentProgressReportAttendanceItemV2
      }
      ratings(filters: $ratingFilters) {
        ...progressReportRatingItem
      }
      remarks(filters: $remarkFilters) {
        ...progressReportRemarkItem
      }
      completedSections
      status
      sectionCompleteStatus {
        section
        isCompleted
      }
    }
    ${progressReportRatingFragment.progressReportRating}
    ${progressReportRemarkFragment.progressReportRemark}
    ${studentFragment.basicStudentDetails}
    ${studentProgressReportAttendanceFragment.studentProgressReportAttendanceV2}
  `,
  studentProgressReportCompletionStatus: gql`
    fragment studentProgressReportCompletionStatus on StudentProgressReport {
      id
      completedSections
    }
  `,
};

export const courseStudentProgressReportFragment = {
  courseStudentProgressReportFeed: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      studentProgressReport(
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        orderByInput: $orderByInput
      ) {
        ...studentProgressReportItem
      }
      reportStatusCount {
        status
        count
      }
    }

    ${studentProgressReportFragment.studentProgressReportFeed}
  `,
  courseStudentProgressReportFeedPaginated: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      studentProgressReports(filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          startCursor
          endCursor
        }
        edges {
          node {
            ...studentProgressReportItem
          }
        }
      }
      reportStatusCount {
        status
        count
      }
    }
    ${studentProgressReportFragment.studentProgressReportFeed}
  `,
  courseStudentProgressReportIds: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      studentProgressReports(filters: $filters) {
        edges {
          node {
            id
            status
          }
        }
      }
    }
  `,
  CourseStudentProgressReportStatusDetails: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      studentProgressReports(filters: $filters) {
        edges {
          node {
            id
            status
            student {
              id
              firstName
              middleName
              lastName
              sourceId
              preferredName
              suffix
              pronouns
              prefix
            }
            publishedReport {
              id
            }
          }
        }
      }
    }
  `,
  courseStudentProgressReportHeaderDetails: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      title
      deadlineDate
      parentEntity {
        ... on Grade {
          id
          name
        }
        ... on Course {
          ...courseItem
        }
      }
      unitPlans {
        id
      }
      reportStatusCount {
        status
        count
      }
    }
    ${courseFragment.basicCourserDetails}
  `,
  courseStudentProgressReportBasicDetails: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      title
      deadlineDate @skip(if: $skipParam)
      course @skip(if: $skipParam) {
        ...courseItem
        grades {
          id
        }
      }
      progressReportTaskGroup {
        id
        academicTerm @skip(if: $skipParam) {
          id
        }
        showStudentReflectionProgress
      }
      parentEntity {
        ... on Grade {
          id
          name
        }
        ... on Course {
          ...courseItem
        }
      }
      unitPlans @skip(if: $skipParam) {
        id
      }
      reportSubjects {
        id
        name
      }
      completionStatusReportCount @skip(if: $skipParam) {
        count
        status
      }
      reportStatusCount @skip(if: $skipParam) {
        status
        count
      }
    }
    ${courseFragment.basicCourserDetails}
  `,
  courseStudentProgressReportTemplate: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      template
    }
  `,
  courseStudentProgressReportMetrics: gql`
    fragment courseStudentProgressReportItem on CourseProgressReportTask {
      id
      title
      parentEntity {
        ... on Grade {
          id
          name
        }
      }
      otherSectionWiseMetrics(filters: $filters) {
        id
        sections
        pending
        total
        notAssessed
      }
      projectSectionWiseMetrics(filters: $filters) {
        id
        sections
        pending
        total
        notAssessed
      }
      subjectWiseMetrics(filters: $filters) @include(if: $isSubjectBased) {
        subject {
          id
          name
        }
        courseWiseStats {
          id
          pending
          total
          notAssessed
          course {
            id
            title
          }
        }
      }
      subjectGroupWiseMetrics @include(if: $param) {
        subjectGroup {
          id
          name
          type
        }
        courseWiseStats {
          id
          pending
          total
          notAssessed
          course {
            id
            title
          }
        }
      }
      progressReportTaskGroup {
        id
        attendanceMode
      }
      learningCourseWiseMetrics(filters: $filters)
        @include(if: $isCourseBased) {
        learningCourse {
          id
          displayName
          type
          academicCourse {
            id
            label
          }
        }
        courseWiseStats {
          id
          pending
          total
          notAssessed
          course {
            id
            title
          }
        }
      }
    }
  `,
};

export const publishedStudentProgressReportFragment = {
  publishedStudentProgressReport: gql`
    fragment publishedStudentProgressReportItem on Student {
      id
      publishedProgressReports {
        id
        filteredNodes
        publishedAt
        studentProgressReport {
          id
          courseProgressReportTask {
            id
            title
            parentEntity {
              id
              ... on Grade {
                id
                name
              }
              ... on Course {
                id
                title
              }
            }
            progressReportTaskGroup {
              id
              title
              academicTerms {
                id
                label
              }
              academicYear {
                id
                startDate
                endDate
                isCurrentAcademicYear
              }
            }
            curriculumProgram {
              id
              type
            }
          }
        }
      }
    }
  `,
};

export const progressReportObjectiveFeedFragment = {
  progressReportObjectiveFeed: gql`
    fragment progressReportObjectiveFeed on ProgressReportTaskGroup {
      id
      gradeSubjectObjectivesWrapper(filters: $filters) {
        grades {
          id
          name
        }
        gradeSubjectObjectives {
          subject {
            id
            name
            associatedChildPlannerElementNodes(
              filters: $associatedChildPlannerElementFilters
            ) {
              id
              label
            }
          }
          gradeObjective {
            grade {
              id
              name
            }
            objective {
              id
              label
            }
          }
        }
      }
    }
  `,
};
export const studentProgressReportConfigurationUnitsFeedFragment = {
  studentProgressReportConfigurationUnitsFeed: gql`
    fragment studentProgressReportConfigurationUnitsFeedItem on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      fields(uids: $fieldUids) {
        ...unitplanFieldBasicDetailsItem
      }
    }
    ${plannerFieldFragment.unitplanFieldBasicDetails}
  `,
  studentProgressReportConfigurationUnitsFeedV2: gql`
    fragment studentProgressReportConfigurationUnitsFeedItemV2 on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      theme {
        id
        resolvedMinimalTree {
          ... on ResolvedFieldThemeSet {
            id
            themes {
              id
              label
            }
          }
        }
      }
      subjects {
        id
        value
      }
    }
  `,
};

export const aiVoiceTonesFragment = {
  progressReportAIVoiceTones: gql`
    fragment progressReportAIVoiceTones on AiVoiceTone {
      id
      title
      description
      type
      sampleComments {
        id
        comment
      }
      outputStructure
      isDefault
      isGlobalDefault
    }
  `,
};

export const reportGradeFragment = {
  basicGradeDetails: gql`
    fragment gradeItem on Grade {
      id
      name
      globalGrade {
        id
        uid
        name
        constants
      }
      students(filters: $gradeStudentsFilters) {
        totalCount
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      displaySequence
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
  `,
};

export const organizationGradesFragment = {
  organizationGrades: gql`
    fragment organizationGradesItem on Organization {
      id
      grades(filters: $filters) {
        ...gradeItem
      }
    }
    ${reportGradeFragment.basicGradeDetails}
  `,
};

export const commentTemplatesFragment = {
  commentTemplates: gql`
    fragment commentTemplates on GradingCommentTemplate {
      id
      title
      comment
      type
      updatedAt
      createdBy {
        id
        profileImage
        firstName
        middleName
        lastName
        prefix
        preferredName
        pronouns
        suffix
      }
      grades {
        id
        name
      }
      subjects {
        id
        name
      }
      characterCount
      mappedTags {
        id
        title
        columnTag {
          id
          title
        }
      }
    }
  `,
};

export const commentTemplatesWithoutPaginationFeedFragment = {
  commentTemplatesWithoutPaginatedFeed: gql`
    fragment commentTemplatesWithoutPaginatedFeed on GradingCommentTemplateConnection {
      totalCount
      createdByStaff {
        id
        firstName
        middleName
        lastName
        profileImage
        prefix
        preferredName
        pronouns
        suffix
      }
      edges {
        node {
          id
          grades {
            id
            name
          }
          subjects {
            id
            name
          }
          type
        }
      }
    }
  `,
  commentTemplatesTags: gql`
    fragment commentTemplatesTags on GradingCommentTemplateTag {
      id
      title
      columnTag {
        id
        title
      }
    }
  `,
};

export const commentTemplatesPaginatedFeedFragment = {
  commentTemplatesPaginatedFeed: gql`
    fragment commentTemplatesPaginatedFeed on GradingCommentTemplateConnection {
      ...commentTemplatesWithoutPaginatedFeed
      pageInfo {
        hasNextPage
        startCursor
        endCursor
      }
      edges {
        node {
          ...commentTemplates
        }
      }
    }
    ${commentTemplatesWithoutPaginationFeedFragment.commentTemplatesWithoutPaginatedFeed}
    ${commentTemplatesFragment.commentTemplates}
  `,
};

export const gradingCommentTemplateExcelFragment = {
  gradingCommentTemplateExcel: gql`
    fragment gradingCommentTemplateExcel on GradingCommentTemplateExcelDownloadResponse {
      id
      url
    }
  `,
};

export const gradeScalesReportsFragment = {
  gradeScalesReportsFragment: gql`
    fragment gradeScalesReportsFragment on GradeScaleConnection {
      edges {
        node {
          id
          templateName
          displayName
          isArchived
          type
          gradingType
          calculateStandardGradesUsing
          minValue
          maxValue
          gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
            id
            gradeScaleValues {
              id
              label
              description
              abbreviation
              displaySequence
              color
              gradeScaleValueScores {
                id
                minValue
                maxValue
                gradeValue
                isMasteryLevel
                subjectLevel {
                  id
                  label
                  abbreviation
                }
              }
            }
          }
        }
      }
    }
  `,
};

export const reportGradeScalesFragment = {
  reportGradeScales: gql`
    fragment reportGradeScalesItem on GradingPeriodGradeScale {
      gradingPeriod {
        id
        label
      }
      gradeScale {
        id
        displayName
      }
      isPrimaryGrade
    }
  `,
  gradeScale: gql`
    fragment gradeScaleItem on GradeScale {
      id
      displayName
    }
  `,
};

export const reportGradingCategorySetsFragment = {
  reportGradingCategorySets: gql`
    fragment reportGradingCategorySetsItem on GradingPeriodGradingCategorySet {
      gradingPeriod {
        id
        label
      }
      gradingCategorySet {
        id
        gradingCategoryValues {
          id
          globalCategory {
            id
            name
          }
          gradeScale {
            id
            displayName
          }
          valueDetails {
            id
            weight
            subjectLevel {
              id
            }
          }
        }
      }
    }
  `,
};

export const standardSetsFragment = {
  standardSets: gql`
    fragment standardSetsItem on StandardSetReport {
      standardSet {
        id
        title
      }
      gradeScales {
        id
        displayName
      }
    }
  `,
};

export const plannerElementNodeSetsFragment = {
  plannerElementNodeSet: gql`
    fragment plannerElementNodeSet on PlannerElementNodeSetReport {
      plannerElementNodeSet {
        id
        title
        subject {
          id
        }
      }
      plannerElementNodeSetType
      gradeScales {
        id
        displayName
      }
    }
  `,
};

export const reportRatingsLearningCourseFragment = {
  reportRatingsLearningCourse: gql`
    fragment reportRatingsLearningCourseItem on StudentProgressReportRatingsWrapper {
      learningCourseRatings {
        id
        value
        isRemoved
        academicCriteriaSet
        academicCriteriaValue
        entityId
        entityType
        contextId
        contextType
        isComputedRating
        academicTermId
        categoryId
        globalCategoryId
        __typename
      }
    }
  `,
};

export const reportRatingsSubjectFragment = {
  reportRatingsSubject: gql`
    fragment reportRatingsSubjectItem on StudentProgressReportRatingsWrapper {
      subjectRatings {
        id
        value
        isRemoved
        academicCriteriaSet
        academicCriteriaValue
        entityId
        entityType
        contextId
        contextType
        isComputedRating
        academicTermId
        categoryId
        globalCategoryId
        __typename
      }
    }
  `,
};

export const reportRatingsUnitFragment = {
  reportRatingsUnit: gql`
    fragment reportRatingsUnitItem on StudentProgressReportRatingsWrapper {
      unitRatings {
        id
        value
        isRemoved
        academicCriteriaSet
        academicCriteriaValue
        entityId
        entityType
        contextId
        contextType
        isComputedRating
        academicTermId
        categoryId
        globalCategoryId
      }
    }
  `,
};

export const reportRatingsAtlsFragment = {
  reportRatingsAtls: gql`
    fragment reportRatingsAtlsItem on StudentProgressReportRatingsWrapper {
      atlSummaryRatings {
        id
        value
        isRemoved
        academicCriteriaSet
        academicCriteriaValue
        entityId
        entityType
        contextId
        contextType
        isComputedRating
        academicTermId
        categoryId
        globalCategoryId
      }
    }
  `,
};

const reportConfigurationSettingsFragment = {
  configurationSettings: gql`
    fragment configurationSettings on ConfigurationSettings {
      scoreDisplayFormat
      isCategoryWeighted
      enableCategoryGrade
      gradingMethodologyTypes
    }
  `,
};
export const learningCourseContextualDataFragment = gql`
  fragment learningCourseContextualData on LearningCourseReport {
    learningCourse {
      id
      title
      primaryGrade {
        id
      }
    }
    reportGradeScales @skip(if: $skipLearningCourses) {
      ...reportGradeScalesItem
    }
    reportRatings @skip(if: $skipLearningCourses) {
      ...reportRatingsLearningCourseItem
    }
    reportGradingCategorySets @skip(if: $skipLearningCourses) {
      ...reportGradingCategorySetsItem
    }
    plannerElementNodeSets @skip(if: $skipLearningCourses) {
      ...plannerElementNodeSet
    }
    standardSets @skip(if: $skipLearningCourses) {
      ...standardSetsItem
    }
    configurationSettings @skip(if: $skipLearningCourses) {
      ...configurationSettings
    }
  }
  ${reportGradeScalesFragment.reportGradeScales}
  ${reportRatingsLearningCourseFragment.reportRatingsLearningCourse}
  ${reportGradingCategorySetsFragment.reportGradingCategorySets}
  ${standardSetsFragment.standardSets}
  ${plannerElementNodeSetsFragment.plannerElementNodeSet}
  ${reportConfigurationSettingsFragment.configurationSettings}
`;

export const subjectContextualDataFragment = gql`
  fragment subjectContextualData on SubjectReport {
    subject {
      id
      name
    }
    reportGradeScales {
      ...reportGradeScalesItem
    }
    reportRatings {
      ...reportRatingsSubjectItem
    }
    reportGradingCategorySets {
      ...reportGradingCategorySetsItem
    }
    plannerElementNodeSets {
      ...plannerElementNodeSet
    }
    standardSets {
      ...standardSetsItem
    }
    configurationSettings {
      ...configurationSettings
    }
  }
  ${reportGradeScalesFragment.reportGradeScales}
  ${reportRatingsSubjectFragment.reportRatingsSubject}
  ${reportGradingCategorySetsFragment.reportGradingCategorySets}
  ${standardSetsFragment.standardSets}
  ${plannerElementNodeSetsFragment.plannerElementNodeSet}
  ${reportConfigurationSettingsFragment.configurationSettings}
`;

export const reportUnitPlanFragment = {
  reportUnitPlan: gql`
    fragment reportUnitPlanItem on UnitPlan {
      id
      title {
        value
      }
    }
  `,
};

export const reportUnitPlanSubjectFragment = {
  reportUnitPlanSubject: gql`
    fragment reportUnitPlanSubjectItem on Subject {
      id
      name
      gradeScales {
        ...gradeScaleItem
      }
    }
    ${reportGradeScalesFragment.gradeScale}
  `,
};

export const unitContextualDataFragment = gql`
  fragment unitContextualData on UnitReport {
    unitPlan {
      ...reportUnitPlanItem
    }
    subjects {
      ...reportUnitPlanSubjectItem
    }
    gradeScales {
      ...gradeScaleItem
    }
    plannerElementNodeSets {
      ...plannerElementNodeSet
    }
    ratings {
      ...reportRatingsUnitItem
    }
  }
  ${reportUnitPlanFragment.reportUnitPlan}
  ${reportUnitPlanSubjectFragment.reportUnitPlanSubject}
  ${reportGradeScalesFragment.gradeScale}
  ${plannerElementNodeSetsFragment.plannerElementNodeSet}
  ${reportRatingsUnitFragment.reportRatingsUnit}
`;

export const atlsContextualDataFragment = gql`
  fragment atlContextualData on ATLReport {
    plannerElementNodeSets {
      ...plannerElementNodeSet
    }
    ratings {
      ...reportRatingsAtlsItem
    }
  }
  ${plannerElementNodeSetsFragment.plannerElementNodeSet}
  ${reportRatingsAtlsFragment.reportRatingsAtls}
`;

export const progressReportPublishedBenchmarkFragment = {
  progressReportPublishedBenchmark: gql`
    fragment progressReportPublishedBenchmarkItem on BenchmarkConnection {
      edges {
        node {
          id
          uid
          label
          subText
          gradingType
          code
          depth
          parentData {
            id
          }
          standardsWrapper {
            properties {
              isArchived
              isPowerStandard
              grades {
                id
                name
              }
              tags {
                id
                label
              }
            }
          }
        }
      }
    }
  `,
};

export const subjectStandardFragment = gql`
  fragment subjectStandardItem on PlannerElementNodeSetConnection {
    edges {
      node {
        id
        title
        isArchived
        gradeScales {
          id
        }
      }
    }
  }
`;

export const learningCoursesReportsFragment = {
  learningCoursesReportsFragment: gql`
    fragment learningCoursesReportsFragment on LearningCourseConnection {
      edges {
        node {
          id
          title
          displayName
          isArchived
          academicCourse {
            id
            label
            code
          }
          subjects {
            id
            name
          }
          subjectGroup {
            id
            name
          }
          associatedLevels {
            id
            label
            abbreviation
          }
        }
      }
    }
  `,
};

export const gradingConfigurationFragment = {
  gradingConfigurationFragment: gql`
    fragment GradingConfigurationFragment on GradingConfiguration {
      id
      settings(filters: $settingsFilters) {
        id
        value
      }
    }
  `,
  scoreGradeAggregationFragment: gql`
    fragment ScoreGradeAggregationFragment on ScoreGradeAggregationConfig {
      id
      gradingType
      gradeScale {
        id
      }
      aggregationTypes
    }
  `,
};
